/**
 * Phase 9.6 — 🛠️ Admin Payouts Dashboard
 * ---------------------------------------
 * Founder/Admin-only. Lists payout requests with filtering, lets the
 * admin approve / deny / mark-paid (with a reference_id).
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "paid", label: "Paid" },
  { id: "denied", label: "Denied" },
  { id: "all", label: "All" },
];

const STATUS_BADGE = {
  pending: "bg-amber-500/20 text-amber-200 border-amber-400/40",
  approved: "bg-blue-500/20 text-blue-200 border-blue-400/40",
  denied: "bg-rose-500/20 text-rose-200 border-rose-400/40",
  paid: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
};

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─────────────────────────────────────────────────────────────
// Payout Safety Panel — Feb 2026, RC 4913e14 UI wiring for the
// already-shipped read-only admin endpoint
// `GET /api/admin/payouts/legacy-exposure`.
// Does not run on page load. Rendered inline above the existing
// payouts tabs. Zero write controls, zero approve/deny surface,
// zero PII (aggregate counts + backfill-provenance breakdown only,
// no user_ids rendered).
// ─────────────────────────────────────────────────────────────
const PayoutSafetyPanel = () => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/admin/payouts/legacy-exposure");
      setData(r?.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load exposure report";
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const agg = data?.aggregate || {};
  const wallets = Array.isArray(data?.wallets) ? data.wallets : [];
  const unverifiedCoins = Number(agg.unverified_total_coins || 0);
  const unverifiedUsd = Number(agg.unverified_total_usd || 0);
  const walletCount = Number(agg.wallet_count || 0);
  const verifiedCoins = Number(agg.verified_total_coins || 0);
  const counterCoins = Number(agg.counter_total_coins || 0);
  const rate = Number(agg.payout_usd_per_earned_coin || 0);

  // Non-PII provenance breakdown — coarse counts only.
  const legacyBackfilled = wallets.filter((w) => w?.was_legacy_backfilled).length;
  const organic = wallets.length - legacyBackfilled;

  // SAFE / REVIEW REQUIRED derived STRICTLY from the response payload.
  const safeStatus = data ? (unverifiedCoins === 0 ? "SAFE" : "REVIEW_REQUIRED") : null;

  return (
    <Card
      className="p-4 mb-4 bg-black/40 border-pink-500/25"
      data-testid="payout-safety-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-black tracking-widest text-pink-200 uppercase">
            🔒 Payout Safety
          </h2>
          <p className="text-[11px] text-pink-200/60 mt-1">
            Read-only S1 legacy-exposure check. Runs the shipped admin endpoint{" "}
            <code className="text-purple-200">/api/admin/payouts/legacy-exposure</code>{" "}
            and summarises whether current available balances are fully backed
            by verified paid-source earnings.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-black"
          data-testid="payout-safety-check-button"
        >
          {busy ? "Checking…" : data ? "🔄 Re-run Check" : "▶ Check Legacy Exposure"}
        </Button>
      </div>

      {err && (
        <div
          className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-100 text-xs p-2"
          data-testid="payout-safety-error"
        >
          {err}
        </div>
      )}

      {data && (
        <div className="mt-4" data-testid="payout-safety-result">
          {/* Status pill */}
          <div className="mb-3">
            {safeStatus === "SAFE" ? (
              <span
                className="inline-block text-xs font-black px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/50 text-emerald-100"
                data-testid="payout-safety-status-safe"
              >
                ✅ SAFE — all available balances fully backed by verified paid entries
              </span>
            ) : (
              <span
                className="inline-block text-xs font-black px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-100"
                data-testid="payout-safety-status-review"
              >
                ⚠️ REVIEW REQUIRED — legacy exposure detected
              </span>
            )}
          </div>

          {/* Aggregate figures — zero PII */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <div
              className="rounded-md bg-white/5 border border-pink-500/15 p-2"
              data-testid="payout-safety-tile-unverified"
            >
              <p className="text-[10px] uppercase tracking-widest text-pink-200/60">
                Legacy Exposure
              </p>
              <p className="text-lg font-black text-amber-200 mt-1">
                {fmtUSD(unverifiedUsd)}
              </p>
              <p className="text-[10px] text-pink-200/50 mt-0.5">
                {unverifiedCoins.toLocaleString()} coins @ {fmtUSD(rate)}/coin
              </p>
            </div>
            <div
              className="rounded-md bg-white/5 border border-pink-500/15 p-2"
              data-testid="payout-safety-tile-wallet-count"
            >
              <p className="text-[10px] uppercase tracking-widest text-pink-200/60">
                Accounts Affected
              </p>
              <p className="text-lg font-black text-white mt-1">
                {walletCount.toLocaleString()}
              </p>
              <p className="text-[10px] text-pink-200/50 mt-0.5">
                {legacyBackfilled.toLocaleString()} legacy-backfilled ·{" "}
                {Math.max(0, organic).toLocaleString()} other
              </p>
            </div>
            <div
              className="rounded-md bg-white/5 border border-pink-500/15 p-2"
              data-testid="payout-safety-tile-verified"
            >
              <p className="text-[10px] uppercase tracking-widest text-pink-200/60">
                Verified Paid-Backed
              </p>
              <p className="text-lg font-black text-emerald-200 mt-1">
                {verifiedCoins.toLocaleString()} coins
              </p>
              <p className="text-[10px] text-pink-200/50 mt-0.5">
                = {fmtUSD(verifiedCoins * rate)}
              </p>
            </div>
            <div
              className="rounded-md bg-white/5 border border-pink-500/15 p-2"
              data-testid="payout-safety-tile-available"
            >
              <p className="text-[10px] uppercase tracking-widest text-pink-200/60">
                Current Available (counters)
              </p>
              <p className="text-lg font-black text-white mt-1">
                {counterCoins.toLocaleString()} coins
              </p>
              <p className="text-[10px] text-pink-200/50 mt-0.5">
                = {fmtUSD(counterCoins * rate)}
              </p>
            </div>
          </div>

          {/* Optional: pending/approved payout exposure — ONLY if the
              endpoint returns it. Current shipped endpoint does not,
              so this block stays hidden. Kept for future-proofing. */}
          {typeof data?.pending_payout_exposure_coins === "number" && (
            <div
              className="mt-3 rounded-md bg-white/5 border border-pink-500/15 p-2 text-xs"
              data-testid="payout-safety-pending-exposure"
            >
              <p className="text-[10px] uppercase tracking-widest text-pink-200/60">
                Pending / Approved Payout Requests Exceeding S1
              </p>
              <p className="text-lg font-black text-amber-200 mt-1">
                {Number(data.pending_payout_exposure_coins).toLocaleString()} coins
                {typeof data?.pending_payout_exposure_usd === "number" && (
                  <> · {fmtUSD(data.pending_payout_exposure_usd)}</>
                )}
              </p>
            </div>
          )}

          <p
            className="mt-3 text-[11px] text-pink-200/50 italic"
            data-testid="payout-safety-footnote"
          >
            Read-only diagnostic. S1 remains the enforced payout gate — this
            panel does not alter any balance or payout state.
          </p>
        </div>
      )}
    </Card>
  );
};

const PayoutHistory = ({ requestId }) => {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (logs !== null) return;
    setLoadingLogs(true);
    try {
      const r = await api.get(`/payouts/${requestId}/history`);
      setLogs(r.data?.logs || []);
    } catch (_e) {
      setLogs([]);
    } finally { setLoadingLogs(false); }
  };

  return (
    <div className="mt-3 border-t border-pink-500/15 pt-2" data-testid={`payout-history-${requestId}`}>
      <button
        type="button"
        onClick={toggle}
        className="text-[11px] text-pink-300 hover:text-pink-200 underline decoration-dotted"
        data-testid={`payout-history-toggle-${requestId}`}
      >
        {open ? "▼" : "▶"} Status history
      </button>
      {open && (
        <div className="mt-2 space-y-1 text-[11px] bg-black/30 rounded p-2 border border-pink-500/10">
          {loadingLogs ? (
            <p className="text-pink-200/60">Loading history…</p>
          ) : logs && logs.length > 0 ? (
            logs.map((log) => (
              <div key={log.log_id} className="flex items-center justify-between gap-2" data-testid={`payout-history-row-${log.log_id}`}>
                <span className="text-pink-100">
                  <span className="text-pink-300 font-semibold capitalize">{log.action}</span>
                  {log.status_after && <span className="text-pink-200/70"> → {log.status_after}</span>}
                  {log.admin_notes && <span className="text-pink-200/60 italic"> · {log.admin_notes}</span>}
                  {log.reference_id && <span className="font-mono text-purple-200"> · ref {log.reference_id}</span>}
                </span>
                <span className="text-pink-200/50">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            ))
          ) : (
            <p className="text-pink-200/60">No history yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

const ActionRow = ({ req, onAction, stripeFunding }) => {
  const [adminNotes, setAdminNotes] = useState(req.admin_notes || "");
  const [referenceId, setReferenceId] = useState(req.reference_id || "");
  const [busy, setBusy] = useState(false);

  const trigger = async (action) => {
    if (busy) return;
    if (action === "mark-paid" && !referenceId.trim()) {
      toast.error("Reference ID required to mark as paid");
      return;
    }
    setBusy(true);
    try {
      await onAction(req.request_id, action, { admin_notes: adminNotes, reference_id: referenceId });
    } finally { setBusy(false); }
  };

  const showApprove = req.status === "pending";
  const showDeny = req.status === "pending" || req.status === "approved";
  const showMarkPaid = req.status === "pending" || req.status === "approved";

  // Phase 17.0 — Stripe Connect rail. Only enabled when requester has a
  // connected Express account with payouts_enabled.
  const requester = req.requester || {};
  const stripeReady = !!(requester.stripe_connect_account_id
                         && requester.stripe_connect_payouts_enabled
                         && requester.stripe_connect_status === "ready");
  const showSendStripe = showMarkPaid && stripeReady;

  if (!showApprove && !showDeny && !showMarkPaid) return null;

  return (
    <div className="mt-3 pt-3 border-t border-pink-500/15 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-pink-200/70">Admin Notes</Label>
          <Textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Optional context for the record"
            rows={1}
            className="bg-black/40 border-pink-500/30 text-white text-xs mt-1"
            data-testid={`admin-payouts-notes-${req.request_id}`}
          />
        </div>
        <div>
          <Label className="text-[10px] text-pink-200/70">Reference ID (required for mark-paid)</Label>
          <Input
            value={referenceId}
            onChange={(e) => setReferenceId(e.target.value)}
            placeholder="paypal_ABC123 / Zelle conf #"
            className="bg-black/40 border-pink-500/30 text-white text-xs mt-1"
            data-testid={`admin-payouts-ref-${req.request_id}`}
          />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {showApprove && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => trigger("approve")}
            className="bg-blue-500/40 hover:bg-blue-500/60 text-blue-100 border border-blue-400/50"
            data-testid={`admin-payouts-approve-${req.request_id}`}
          >
            ✓ Approve
          </Button>
        )}
        {showDeny && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => trigger("deny")}
            className="bg-rose-500/40 hover:bg-rose-500/60 text-rose-100 border border-rose-400/50"
            data-testid={`admin-payouts-deny-${req.request_id}`}
          >
            ✕ Deny
          </Button>
        )}
        {showMarkPaid && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => trigger("mark-paid")}
            className="bg-emerald-500/40 hover:bg-emerald-500/60 text-emerald-100 border border-emerald-400/50"
            data-testid={`admin-payouts-mark-paid-${req.request_id}`}
          >
            💰 Mark Paid (Manual)
          </Button>
        )}
        {showSendStripe && (() => {
          // Feb 2026 — gate Send via Stripe behind the LIVE funding preflight.
          const requested = Number(req.amount_usd || 0);
          const avail = Number(stripeFunding?.available_usd || 0);
          const probeOk = !!stripeFunding?.ok;
          const sufficient = probeOk && avail + 1e-9 >= requested;
          const shortage = Math.max(0, requested - avail).toFixed(2);
          const disabled = busy || !sufficient;
          const title = !probeOk
            ? "Stripe funding status unavailable — resolve the probe before sending"
            : sufficient
              ? `Transfer to creator's Stripe Connect account ($${avail.toFixed(2)} available)`
              : `Insufficient Stripe funds — short $${shortage}. Top up before sending.`;
          return (
            <>
              <Button
                size="sm"
                disabled={disabled}
                onClick={() => trigger("send-via-stripe")}
                className="bg-indigo-500/40 hover:bg-indigo-500/60 text-indigo-100 border border-indigo-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`admin-payouts-send-stripe-${req.request_id}`}
                title={title}
              >
                ⚡ Send via Stripe
              </Button>
              {probeOk && sufficient && (
                <span
                  className="text-[10px] self-center font-bold text-emerald-200 bg-emerald-500/15 border border-emerald-400/40 px-2 py-0.5 rounded-full"
                  data-testid={`admin-payouts-funding-ok-${req.request_id}`}
                  title={`Requested $${requested.toFixed(2)} ≤ Stripe available $${avail.toFixed(2)}`}
                >
                  ✅ Funds Available
                </span>
              )}
              {probeOk && !sufficient && (
                <span
                  className="text-[10px] self-center font-bold text-rose-200 bg-rose-500/15 border border-rose-400/40 px-2 py-0.5 rounded-full"
                  data-testid={`admin-payouts-funding-short-${req.request_id}`}
                  title={`Need $${requested.toFixed(2)}, Stripe has $${avail.toFixed(2)}`}
                >
                  ⛔ Insufficient Stripe Funds — Short ${shortage}
                </span>
              )}
              {!probeOk && (
                <span
                  className="text-[10px] self-center italic text-rose-200/80"
                  data-testid={`admin-payouts-funding-probe-fail-${req.request_id}`}
                >
                  (funding status unavailable)
                </span>
              )}
            </>
          );
        })()}
        {showMarkPaid && !stripeReady && (
          <span
            className="text-[10px] text-gray-400 self-center italic"
            data-testid={`admin-payouts-stripe-not-ready-${req.request_id}`}
            title={requester.stripe_connect_status === "restricted"
              ? `Creator's Stripe account restricted: ${requester.stripe_connect_disabled_reason || "needs info"}`
              : "Creator hasn't connected a Stripe payout account yet"}
          >
            (Stripe: {requester.stripe_connect_status || "not connected"})
          </span>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Feb 2026 — Stripe LIVE available-balance preflight card
// Shows the authoritative Stripe platform balance (admin-only) and
// disables Send via Stripe when the balance can't cover the request.
// NOT exposed to artists. Uses /admin/payouts/stripe-funding-status.
// ─────────────────────────────────────────────────────────────
const StripeFundingCard = ({ data, onRefresh }) => {
  if (!data) {
    return (
      <Card
        className="p-3 mb-4 bg-slate-500/10 border-slate-500/30 text-slate-100 text-xs"
        data-testid="stripe-funding-card-loading"
      >
        Loading Stripe funding status…
      </Card>
    );
  }
  const live = data.key_mode === "live";
  const ok = !!data.ok;
  const modeChip = (
    <span
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
        live
          ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
          : "bg-amber-500/20 text-amber-200 border-amber-400/40"
      }`}
      data-testid="stripe-funding-mode"
    >
      {live ? "LIVE" : (data.key_mode || "unknown").toUpperCase()}
    </span>
  );
  if (!ok) {
    return (
      <Card
        className="p-3 mb-4 bg-rose-500/10 border-rose-500/30 text-rose-100 text-xs"
        data-testid="stripe-funding-card-unavailable"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <p className="font-bold">⛔ Stripe funding status unavailable — payout not sent.</p>
            <p className="opacity-90 mt-1">
              {data.error?.message || "Platform balance could not be verified."} Send
              via Stripe is disabled for every request until this clears.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {modeChip}
            <button
              type="button"
              onClick={onRefresh}
              data-testid="stripe-funding-refresh"
              className="text-[10px] underline opacity-80 hover:opacity-100"
            >
              Retry
            </button>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card
      className="p-3 mb-4 bg-indigo-500/10 border-indigo-500/30 text-indigo-100 text-xs"
      data-testid="stripe-funding-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-bold">
            ⚡ Stripe Platform Available:{" "}
            <span className="text-white" data-testid="stripe-funding-available">
              ${Number(data.available_usd || 0).toFixed(2)}
            </span>
          </p>
          <p className="opacity-80 mt-1">
            Authoritative LIVE balance from stripe.Balance (
            <code className="bg-black/50 px-1 rounded">available.usd</code>). Pending ${
              Number(data.pending_usd || 0).toFixed(2)
            }. Send via Stripe is gated against this number — the RGM reserve
            is a separate check.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {modeChip}
          <button
            type="button"
            onClick={onRefresh}
            data-testid="stripe-funding-refresh"
            className="text-[10px] underline opacity-80 hover:opacity-100"
          >
            Refresh
          </button>
        </div>
      </div>
    </Card>
  );
};


const AdminPayoutsPage = () => {
  const { authChecked } = useRequireAdmin();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  // Feb 2026 — Stripe LIVE available-balance preflight. Shared across
  // the top-of-page card and the per-row Send-via-Stripe gate so admins
  // see a single source of truth. Refreshed on tab switches + after any
  // action completes.
  const [stripeFunding, setStripeFunding] = useState(null);
  const navigate = useNavigate();

  const refreshStripeFunding = async () => {
    try {
      const r = await api.get("/admin/payouts/stripe-funding-status");
      setStripeFunding(r?.data || null);
    } catch (e) {
      // Fail-closed: a probe error means Send-via-Stripe is blocked.
      setStripeFunding({
        ok: false,
        available_usd: 0,
        key_mode: "unknown",
        error: {
          code: "stripe_funding_probe_failed",
          message: e?.response?.data?.detail?.message || e?.message || "Probe failed",
        },
      });
    }
  };

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payouts/list?status=${status}&limit=200`);
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error("Couldn't load payouts");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(tab); refreshStripeFunding(); }, [tab]); // eslint-disable-line

  const handleAction = async (request_id, action, payload) => {
    try {
      await api.post(`/admin/payouts/${request_id}/${action}`, payload);
      toast.success(`Request ${action.replace("-", " ")}`);
      await load(tab);
      await refreshStripeFunding();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Structured insufficient-funds / mode / probe errors.
      if (d && typeof d === "object") {
        if (d.code === "insufficient_platform_funds") {
          toast.error(
            `⛔ Stripe short $${Number(d.shortage_usd).toFixed(2)} — ` +
            `requested $${Number(d.requested_usd).toFixed(2)}, ` +
            `available $${Number(d.stripe_available_usd).toFixed(2)}`
          );
          await refreshStripeFunding();
          return;
        }
        if (d.code === "stripe_mode_not_live" || d.code === "stripe_funding_status_unavailable") {
          toast.error(d.message || "Stripe funding status unavailable — payout not sent.");
          await refreshStripeFunding();
          return;
        }
      }
      toast.error(typeof d === "string" ? d : "Action failed");
    }
  };

  if (denied) {
    return (
      <div className="min-h-screen bg-[#0a0514] text-white flex items-center justify-center p-6">
        <Card className="max-w-md p-6 bg-rose-500/10 border-rose-400/40 text-center">
          <h1 className="text-2xl font-black text-white mb-2">🔒 Admin only</h1>
          <p className="text-rose-200/80 text-sm">
            Only founders/admins can manage payouts.
          </p>
          <Button onClick={() => navigate("/")} className="mt-4 bg-rose-600 hover:bg-rose-500">
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-payouts-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">🛠️ Admin Payouts</h1>
            <p className="text-pink-200/80 text-sm mt-1">
              Review, approve / deny, and mark creator payouts as paid.
            </p>
          </div>
          <Link to="/admin" className="text-xs text-pink-300 hover:text-pink-200 underline">← Admin</Link>
        </div>

        {/* Phase 15.6Q — Stripe webhook config notice */}
        <Card className="p-3 mb-4 bg-amber-500/10 border-amber-500/30 text-amber-100 text-xs" data-testid="stripe-webhook-warning">
          <p className="font-bold">⚠️ Production checklist</p>
          <p className="opacity-90 mt-1">
            Before going live with real card payments, set <code className="bg-black/50 px-1 rounded">STRIPE_WEBHOOK_SECRET</code> in production env and point Stripe → Webhooks at <code className="bg-black/50 px-1 rounded">https://rgmstudio.org/api/webhook/beats</code> for the <code className="bg-black/50 px-1 rounded">checkout.session.completed</code> event. Without it, beat-sale credits will rely solely on buyer return-page polling.
          </p>
        </Card>

        {/* Feb 2026 — RC 4913e14 UI wiring for the shipped read-only
            legacy-exposure diagnostic. Does NOT run on page load. */}
        <PayoutSafetyPanel />

        {/* Feb 2026 — Stripe LIVE available-balance preflight card. */}
        <StripeFundingCard data={stripeFunding} onRefresh={refreshStripeFunding} />

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-black/40 border border-pink-500/30 rounded-full p-1 w-fit" data-testid="admin-payouts-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                tab === t.id
                  ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow"
                  : "text-pink-200/70 hover:text-white"
              }`}
              data-testid={`admin-payouts-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-pink-200/60 text-sm text-center py-12">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center bg-black/40 border-pink-500/20" data-testid="admin-payouts-empty">
            <p className="text-pink-200/60 text-sm">No {tab !== "all" ? tab : ""} payout requests.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((p) => (
              <Card
                key={p.request_id}
                className="p-4 bg-black/40 border-pink-500/20"
                data-testid={`admin-payouts-row-${p.request_id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <img
                    src={getProfilePhotoUrl(p.requester || {})}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border border-pink-500/30"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white">
                      {p.requester?.artist_name || p.artist_name || p.requester?.name || "—"}{" "}
                      <span className="text-pink-200/60 text-xs font-normal">
                        ({p.legal_name})
                      </span>
                      {p.country && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-200">
                          🌍 {p.country}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-pink-200/70 truncate">
                      {p.requester?.email || p.user_id}
                    </p>
                    <p className="text-[11px] text-pink-200/60 mt-1">
                      {fmtUSD(p.amount_usd)} via{" "}
                      <span className="text-white font-semibold">{p.method}</span>
                      {p.network && (
                        <span className="ml-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-400/40 text-amber-200">
                          {p.network}
                        </span>
                      )}
                      {" "}
                      {p.method === "usdt" && p.wallet_address ? (
                        <>
                          {" → "}
                          <span
                            className="font-mono text-purple-200 break-all cursor-pointer hover:text-purple-100 underline decoration-dotted"
                            title="Click to copy"
                            onClick={(e) => {
                              e.preventDefault();
                              navigator.clipboard?.writeText(p.wallet_address);
                              toast.success("Wallet address copied");
                            }}
                            data-testid={`admin-payouts-wallet-${p.request_id}`}
                          >
                            {p.wallet_address}
                          </span>
                        </>
                      ) : (
                        <>
                          {" to "}
                          <span className="font-mono text-purple-200">{p.handle}</span>
                        </>
                      )}
                    </p>
                    {p.bank_details && (
                      <pre className="text-[10px] text-pink-100/80 bg-black/40 p-2 rounded mt-2 max-h-32 overflow-auto whitespace-pre-wrap font-mono border border-pink-500/20" data-testid={`admin-payouts-bank-${p.request_id}`}>
                        {p.bank_details}
                      </pre>
                    )}
                    {p.notes && (
                      <p className="text-[11px] text-pink-200/70 italic mt-1">&ldquo;{p.notes}&rdquo;</p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_BADGE[p.status]}`}
                      data-testid={`admin-payouts-status-${p.request_id}`}
                    >
                      {p.status}
                    </span>
                    {/* Phase 17.0 — payment-rail badge (Stripe vs Manual) */}
                    {(() => {
                      const rail = p.payment_rail === "stripe_connect" ? "stripe" : "manual";
                      const cls = rail === "stripe"
                        ? "bg-indigo-500/20 text-indigo-200 border-indigo-400/40"
                        : "bg-slate-500/20 text-slate-200 border-slate-400/40";
                      const label = rail === "stripe" ? "⚡ Stripe Connect" : "✉ Manual";
                      return (
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${cls}`}
                          data-testid={`admin-payouts-rail-${p.request_id}`}
                          title={rail === "stripe"
                            ? `Stripe transfer ${p.stripe_transfer_id || ""}`
                            : "Paid out-of-band (PayPal/Zelle/Bank/etc.) and recorded manually"}
                        >
                          {label}
                        </span>
                      );
                    })()}
                    {(() => {
                      // Phase 16.0 — Always render a source badge so admins
                      // can instantly distinguish Live/Gifts ($577 coin pool)
                      // from Beat marketplace ($ producer pool). Legacy
                      // requests without a `source` field are treated as
                      // gifts (the only source before Phase 15.6Q).
                      const isBeats = p.source === "beat_sales";
                      const label = isBeats ? "💎 Beats" : "🎁 Gifts / Live";
                      const cls = isBeats
                        ? "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40"
                        : "bg-purple-500/20 text-purple-200 border-purple-400/40";
                      return (
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${cls}`}
                          data-testid={`admin-payouts-source-${p.request_id}`}
                          title={isBeats
                            ? "Funded by Beat Marketplace producer_balances (USD direct)"
                            : "Funded by Live/gift coin_wallets withdrawable earnings ($0.0075/coin)"}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <PayoutHistory requestId={p.request_id} />
                <ActionRow req={p} onAction={handleAction} stripeFunding={stripeFunding} />
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayoutsPage;
